import "./Home.css";

import { useEffect, useRef, useState } from "react";

import CodeExampleTabs from "../components/helpers/CodeExampleTabs";
import Header from "../components/layout/Header";
import Plans from "../components/layout/home/Plans";
import { motion } from "framer-motion";
import { useCases } from "../components/layout/home/UseCases";
import { useNavigate } from "react-router-dom";

const HeroPlansSlide = ({
  token,
  setToken,
  selectedPlan,
  setSelectedPlan,
  navigate,
}) => (
  <div>
    <Header nologo noconsole />
    <div className="min-h-screen flex flex-col items-start bg-gray-50 px-4 py-4 sm:px-6">
      {/* Title & Intro */}
      <h1 className="w-full text-4xl sm:text-5xl font-bold text-center leading-tight">
        loghook.net
      </h1>
      <p className="text-base sm:text-3xl text-gray-600 max-w-2xl mx-auto my-4">
        Beautifully simple event monitoring
      </p>

      {/* Plans selector */}
      <Plans
        token={token}
        setToken={setToken}
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
        navigate={navigate}
      />

      {/* Code‑example block */}
      <div className="w-full flex flex-col md:flex-row items-center gap-6 mt-8 bg-white rounded-xl p-6">
        {/* Text intro */}
        <div className="w-full md:w-1/2 order-2 md:order-1">
          <h3 className="text-xl sm:text-2xl font-semibold mb-3 text-center md:text-left">
            One Endpoint. Infinite Use Cases.
          </h3>
          <p className="text-sm sm:text-base text-gray-600 text-center md:text-left">
            Whether it’s a cron job, microservice, IoT device, or just a local
            script — Loghook gives you one simple API to monitor it all.
          </p>
        </div>

        {/* Tabs */}
        <div className="w-full md:w-1/2 order-1 md:order-2 overflow-x-auto">
          <CodeExampleTabs token={token} />
        </div>
      </div>
    </div>
  </div>
);

// Helper function to generate a random Base64 token
const generateRandomToken = () => {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
    ""
  ); // Convert to hex
};

function Home() {
  const [token, setToken] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("guest");
  const slides = [HeroPlansSlide, ...useCases];
  const navigate = useNavigate();

  const containerRef = useRef(null);
  const [snapEnabled, setSnapEnabled] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setSnapEnabled(true), 200);
    return () => clearTimeout(timeout);
  }, []);

  // Track which slide is currently visible using IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
      },
      {
        root: containerRef.current,
        threshold: 0.6,
      }
    );

    const sections = containerRef.current?.querySelectorAll(".snap-section");
    sections?.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [snapEnabled]);

  const user = null;

  useEffect(() => {
    if (!user) {
      const newToken = generateRandomToken();
      setToken(newToken);
    }
  }, [user]);

  return (
    <div className="bg-white relative overflow-hidden">
      <motion.section
        ref={containerRef}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        className={`h-screen overflow-y-scroll bg-white ${
          snapEnabled ? "snap-y snap-mandatory" : ""
        }`}
      >
        {slides.map((Slide, index) => (
          <motion.section
            key={index}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className={` ${index == 0 ? "snap-end" : "snap-start"}`}
            data-index={index}
          >
            <Slide
              token={token}
              setToken={setToken}
              selectedPlan={selectedPlan}
              setSelectedPlan={setSelectedPlan}
              navigate={navigate}
            />
          </motion.section>
        ))}
      </motion.section>
    </div>
  );
}

export default Home;
